/**
 * Helpers purs pour manipuler les blocs V2 côté éditeur.
 */

import type { ExerciseVisualBlock } from '@jeraisonne/ai';
import type {
  BlockMutationOp,
  ExerciseBlock,
  ExerciseBlockSection,
  ExerciseBlockType,
} from './types';

/** Extrait `data.visuals[]` d'un bloc (visuels V2 embarqués, cf. migration 0096). */
export function visualsOf(block: ExerciseBlock): ExerciseVisualBlock[] {
  const raw = (block.data as { visuals?: unknown }).visuals;
  return Array.isArray(raw) && raw.length > 0 ? (raw as ExerciseVisualBlock[]) : [];
}

/** Tous les visuels V2 d'un exercice, tous blocs confondus (statement en tête, ordre stable). */
export function allVisualsOf(blocks: ExerciseBlock[]): ExerciseVisualBlock[] {
  return blocks.flatMap(visualsOf);
}

/** Renvoie le texte principal éditable d'un bloc (selon son type) et le champ. */
export function getPrimaryTextField(
  blockType: ExerciseBlockType,
): 'content_md' | 'prompt_md' | 'explanation_md' | 'body_md' | 'label_md' | 'intro_md' {
  switch (blockType) {
    case 'statement':
      return 'content_md';
    case 'statement_question':
      return 'prompt_md';
    case 'correction':
      return 'explanation_md';
    case 'distractor':
      return 'label_md';
    case 'explanation':
    case 'hint':
    case 'note':
      return 'body_md';
    case 'clue_hunt':
      return 'intro_md';
  }
}

/** Trie les blocs par (section, position) dans l'ordre macro statement→correction→meta. */
export function sortBlocks(blocks: ExerciseBlock[]): ExerciseBlock[] {
  const sectionRank: Record<ExerciseBlockSection, number> = {
    statement: 0,
    correction: 1,
    meta: 2,
  };
  return [...blocks].sort((a, b) => {
    const s = sectionRank[a.section] - sectionRank[b.section];
    if (s !== 0) return s;
    return a.position - b.position;
  });
}

/** Filtre les blocs d'une section donnée, triés par position. */
export function blocksInSection(
  blocks: ExerciseBlock[],
  section: ExerciseBlockSection,
): ExerciseBlock[] {
  return blocks.filter((b) => b.section === section).sort((a, b) => a.position - b.position);
}

/** Calcule une position pour insérer après le bloc d'index `i` dans une liste déjà triée. */
export function positionAfter(sectionBlocks: ExerciseBlock[], indexAfter: number): number {
  const cur = sectionBlocks[indexAfter];
  const next = sectionBlocks[indexAfter + 1];
  if (!cur) return 1;
  if (!next) return cur.position + 1;
  return (cur.position + next.position) / 2;
}

/** Calcule une position pour insérer avant le bloc d'index `i`. */
export function positionBefore(sectionBlocks: ExerciseBlock[], indexBefore: number): number {
  const cur = sectionBlocks[indexBefore];
  const prev = sectionBlocks[indexBefore - 1];
  if (!cur) return 1;
  if (!prev) return cur.position / 2;
  return (prev.position + cur.position) / 2;
}

/** Position d'append à la fin d'une section. */
export function positionAtEnd(sectionBlocks: ExerciseBlock[]): number {
  if (sectionBlocks.length === 0) return 1;
  return sectionBlocks[sectionBlocks.length - 1]!.position + 1;
}

/** Renvoie les enfants directs (parent_id = parentId) triés par position. */
export function childrenOf(blocks: ExerciseBlock[], parentId: string): ExerciseBlock[] {
  return blocks.filter((b) => b.parent_id === parentId).sort((a, b) => a.position - b.position);
}

/**
 * Ids d'un bloc ET de tous ses descendants (BFS : racine, enfants, petits-enfants…).
 * Pur. Sert à supprimer un sous-arbre en cascade (un corrigé + ses distracteurs /
 * explications enfants).
 */
export function collectSubtreeIds(blocks: ExerciseBlock[], rootId: string): string[] {
  const ids: string[] = [];
  const queue = [rootId];
  while (queue.length > 0) {
    const id = queue.shift()!;
    ids.push(id);
    for (const child of childrenOf(blocks, id)) queue.push(child.id);
  }
  return ids;
}

/**
 * Ops de suppression d'un bloc et de tout son sous-arbre, ENFANTS D'ABORD.
 * Indispensable : la FK `parent_id` est `ON DELETE SET NULL` et le trigger de
 * topologie refuse un `distractor`/`hint` sans parent — supprimer le parent en
 * premier mettrait ses enfants orphelins et ferait échouer la suppression. En
 * supprimant les feuilles d'abord, le parent n'a plus d'enfant à orphéliner.
 * Pur — testable.
 */
export function buildDeleteSubtreeOps(blocks: ExerciseBlock[], rootId: string): BlockMutationOp[] {
  return collectSubtreeIds(blocks, rootId)
    .reverse()
    .map((id) => ({ op: 'delete', id }));
}

/** Vrai si la correction est un point de contrôle QCM. */
export function isQcmCheckpoint(block: ExerciseBlock): boolean {
  if (block.block_type !== 'correction') return false;
  if (block.parent_id === null) return false;
  const ans = (block.data as Record<string, unknown>).answer_md;
  return typeof ans === 'string' && ans.length > 0;
}

/**
 * Checkpoints QCM (corrections avec `answer_md`) enfants d'une question, triés
 * par position — c.-à-d. les ÉTAPES du corrigé pas-à-pas, dans l'ordre. La
 * dernière porte la réponse finale. Une question « one-shot » (corrigé V1→V2 ou
 * ancien modèle) renvoie un seul élément → rétro-compatible.
 */
export function orderedCheckpointsFor(
  blocks: ExerciseBlock[],
  questionId: string,
): ExerciseBlock[] {
  return childrenOf(blocks, questionId).filter(isQcmCheckpoint);
}

/** Mode de réponse d'un checkpoint. Défaut 'single' (rétro-compatible). */
export function checkpointAnswerMode(
  checkpoint: ExerciseBlock,
): 'single' | 'multiple' | 'calculator' {
  const mode = (checkpoint.data as { answer_mode?: unknown }).answer_mode;
  return mode === 'multiple' || mode === 'calculator' ? mode : 'single';
}

/** Variable disponible dans la calculatrice-réponse (paramètre d'énoncé ou résultat d'une question précédente). */
export interface CalculatorVariable {
  label: string;
  value: number;
}

/** Données d'un checkpoint en mode 'calculator' — cf. answer_mode ci-dessus. Générées par le Blueprint paramétrique. */
export function calculatorCheckpointData(checkpoint: ExerciseBlock): {
  expectedValue: number;
  tolerance: number;
  unitLabel?: string;
  availableVariables: CalculatorVariable[];
} | null {
  const data = checkpoint.data as {
    expected_value?: unknown;
    tolerance?: unknown;
    unit_label?: unknown;
    available_variables?: unknown;
  };
  if (typeof data.expected_value !== 'number' || typeof data.tolerance !== 'number') return null;
  const availableVariables = Array.isArray(data.available_variables)
    ? (data.available_variables as CalculatorVariable[]).filter(
        (v) => v && typeof v.label === 'string' && typeof v.value === 'number',
      )
    : [];
  return {
    expectedValue: data.expected_value,
    tolerance: data.tolerance,
    unitLabel: typeof data.unit_label === 'string' ? data.unit_label : undefined,
    availableVariables,
  };
}

/**
 * Ensemble des ids de blocs qui constituent une BONNE réponse pour un checkpoint :
 * la correction elle-même (toujours correcte) + ses distracteurs enfants marqués
 * `data.is_correct === true`. En mode 'single', renvoie juste `[checkpoint.id]`.
 * Pur — testable.
 */
export function correctChoiceIds(blocks: ExerciseBlock[], checkpoint: ExerciseBlock): string[] {
  const extra = childrenOf(blocks, checkpoint.id)
    .filter(
      (b) =>
        b.block_type === 'distractor' && (b.data as { is_correct?: unknown }).is_correct === true,
    )
    .map((b) => b.id);
  return [checkpoint.id, ...extra];
}

/**
 * Égalité d'ensembles (tout-ou-rien) : vrai si l'ensemble coché correspond
 * EXACTEMENT à l'ensemble correct (mêmes ids, sans surplus ni manque). Ignore
 * l'ordre et les doublons. Pur — testable.
 */
export function isAnswerSetCorrect(
  chosenIds: readonly string[],
  correctIds: readonly string[],
): boolean {
  const chosen = new Set(chosenIds);
  const correct = new Set(correctIds);
  if (chosen.size !== correct.size) return false;
  for (const id of correct) if (!chosen.has(id)) return false;
  return true;
}

/**
 * Un « pas » jouable du player V2. Une `statement_question` à corrigé pas-à-pas
 * se déploie en PLUSIEURS pas (un par checkpoint/étape) ; un bloc `statement`
 * (sous-énoncé) ou une question sans checkpoint = un seul pas d'affichage.
 */
export interface PlayStep {
  /** Bloc affiché (la question ou un sous-énoncé). */
  block: ExerciseBlock;
  /** Le sous-QCM de ce pas (null = affichage seul, pas de QCM). */
  checkpoint: ExerciseBlock | null;
  /** Afficher l'énoncé du bloc (vrai pour un statement, ou le 1er pas d'une question). */
  showPrompt: boolean;
  /**
   * Sous-énoncés (`statement`) placés juste AVANT la question : ils font partie
   * de CETTE question et s'affichent dans sa carte, au-dessus de son énoncé
   * (1er pas seulement). Vide pour un pas sans question.
   */
  leadIn: ExerciseBlock[];
  /** Index 0-based du checkpoint dans sa question (0 si aucun). */
  stepIndex: number;
  /** Nombre de checkpoints de la question (0 si aucun, 1 = question one-shot). */
  stepCount: number;
}

/**
 * Déploie les blocs jouables (section statement hors situation) en pas du player :
 * chaque `statement_question` engendre UN pas par checkpoint (corrigé pas-à-pas) ;
 * l'énoncé n'est affiché que sur le 1er pas de la question. Un sous-énoncé
 * `statement` placé AVANT une question appartient à cette question : il est
 * porté par son 1er pas (`leadIn`), jamais affiché seul à la suite de la question
 * précédente. Un sous-énoncé sans question après lui (fin d'exercice, ou suivi
 * d'un autre type de bloc) reste un pas d'affichage seul. Pur — testable.
 */
export function buildPlaySteps(stepBlocks: ExerciseBlock[], blocks: ExerciseBlock[]): PlayStep[] {
  const out: PlayStep[] = [];
  let pending: ExerciseBlock[] = [];
  const flushPending = () => {
    for (const b of pending) {
      out.push({
        block: b,
        checkpoint: null,
        showPrompt: true,
        leadIn: [],
        stepIndex: 0,
        stepCount: 0,
      });
    }
    pending = [];
  };
  for (const b of stepBlocks) {
    if (b.block_type === 'statement') {
      pending.push(b);
      continue;
    }
    if (b.block_type !== 'statement_question') {
      flushPending();
      out.push({
        block: b,
        checkpoint: null,
        showPrompt: true,
        leadIn: [],
        stepIndex: 0,
        stepCount: 0,
      });
      continue;
    }
    const leadIn = pending;
    pending = [];
    const checkpoints = orderedCheckpointsFor(blocks, b.id);
    if (checkpoints.length === 0) {
      out.push({
        block: b,
        checkpoint: null,
        showPrompt: true,
        leadIn,
        stepIndex: 0,
        stepCount: 0,
      });
      continue;
    }
    checkpoints.forEach((cp, i) =>
      out.push({
        block: b,
        checkpoint: cp,
        showPrompt: i === 0,
        leadIn: i === 0 ? leadIn : [],
        stepIndex: i,
        stepCount: checkpoints.length,
      }),
    );
  }
  flushPending();
  return out;
}

/** Renvoie le bloc statement_question référencé par une correction. */
export function questionForCorrection(
  blocks: ExerciseBlock[],
  correction: ExerciseBlock,
): ExerciseBlock | null {
  if (correction.parent_id === null) return null;
  return blocks.find((b) => b.id === correction.parent_id) ?? null;
}

/** Liste les statement_question d'un exercice, triés par position. */
export function listQuestions(blocks: ExerciseBlock[]): ExerciseBlock[] {
  return blocks
    .filter((b) => b.block_type === 'statement_question')
    .sort((a, b) => a.position - b.position);
}

/**
 * État de complétude de la génération V2, dérivé des blocs.
 *
 * Sert au panneau de génération de l'éditeur (quoi reste à générer) ET au
 * résumé compact de la page de détail. Pur — testable.
 */
export interface V2GenerationState {
  /** Toutes les questions (`statement_question`). */
  questions: ExerciseBlock[];
  /** Toutes les corrections (`correction`, étape ou QCM). */
  corrections: ExerciseBlock[];
  /** Corrections QCM (parent_id ≠ null + answer_md). */
  qcmCheckpoints: ExerciseBlock[];
  /** Nombre total de distracteurs. */
  distractorsCount: number;
  /** Questions sans aucune correction enfant. */
  questionsWithoutCorrection: ExerciseBlock[];
  /** Corrigés QCM sans distracteur enfant. */
  qcmWithoutDistractors: ExerciseBlock[];
}

export function v2GenerationState(blocks: ExerciseBlock[]): V2GenerationState {
  const questions = listQuestions(blocks);
  const corrections = blocks
    .filter((b) => b.block_type === 'correction')
    .sort((a, b) => a.position - b.position);
  const qcmCheckpoints = corrections.filter(isQcmCheckpoint);
  const distractorsCount = blocks.filter((b) => b.block_type === 'distractor').length;

  const questionsWithoutCorrection = questions.filter(
    (q) => !corrections.some((c) => c.parent_id === q.id),
  );
  const qcmWithoutDistractors = qcmCheckpoints.filter(
    (c) => !childrenOf(blocks, c.id).some((b) => b.block_type === 'distractor'),
  );

  return {
    questions,
    corrections,
    qcmCheckpoints,
    distractorsCount,
    questionsWithoutCorrection,
    qcmWithoutDistractors,
  };
}

/**
 * Construit les ops pour « isoler » un visuel : le retirer de son bloc source et
 * le placer SEUL dans un nouveau bloc `statement` (section statement, racine).
 *
 * - Le nouveau bloc est inséré juste après le bloc source s'il est dans la
 *   section statement, sinon à la fin de cette section.
 * - `content_md` est requis non-vide par le schéma : on reprend le titre du
 *   visuel s'il existe, sinon un libellé éditable par défaut.
 * - Renvoie `[]` si le visuel est introuvable (no-op).
 */
export function buildIsolateVisualOps(
  blocks: ExerciseBlock[],
  sourceBlock: ExerciseBlock,
  visualId: string,
  newClientId: string,
): BlockMutationOp[] {
  const data = sourceBlock.data as Record<string, unknown>;
  const visuals = Array.isArray(data.visuals)
    ? (data.visuals as Array<Record<string, unknown>>)
    : [];
  const target = visuals.find((v) => (v as { id?: unknown }).id === visualId);
  if (!target) return [];
  const remaining = visuals.filter((v) => (v as { id?: unknown }).id !== visualId);

  const stmtList = blocksInSection(blocks, 'statement');
  const idx = stmtList.findIndex((b) => b.id === sourceBlock.id);
  const newPos = idx >= 0 ? positionAfter(stmtList, idx) : positionAtEnd(stmtList);

  const title = typeof target.title === 'string' ? target.title.trim() : '';
  const content_md = title || 'Visuel';
  const isolatedVisual = { ...target, order: 0 };

  const newData: Record<string, unknown> = { ...data };
  if (remaining.length === 0) delete newData.visuals;
  else newData.visuals = remaining;

  return [
    { op: 'update', id: sourceBlock.id, patch: { data: newData } },
    {
      op: 'insert',
      client_id: newClientId,
      block_type: 'statement',
      section: 'statement',
      parent_id: null,
      position: newPos,
      data: { content_md, visuals: [isolatedVisual] },
    },
  ];
}
